import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid, Card, CardContent, Typography, Box, Button,
  List, ListItem, ListItemText, ListItemIcon, Chip
} from '@mui/material';
import {
  Pages, Article, LocalHospital, Comment, Add,
  TrendingUp, Schedule
} from '@mui/icons-material';
import { getPages, getArticles, getCentres, getTemoignages } from '../services/api';

function Dashboard() {
  const [stats, setStats] = useState({
    pages: 0,
    articles: 0,
    centres: 0,
    temoignagesPending: 0
  });
  const [recentPages, setRecentPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pagesRes, articlesRes, centresRes, temoignagesRes] = await Promise.all([
        getPages(),
        getArticles(),
        getCentres(),
        getTemoignages({ status: 'pending' })
      ]);

      setStats({
        pages: pagesRes.data.length,
        articles: articlesRes.data.articles?.length || articlesRes.data.length,
        centres: centresRes.data.length,
        temoignagesPending: temoignagesRes.data.length
      });

      setRecentPages(pagesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Pages', value: stats.pages, icon: <Pages />, color: '#2563eb', path: '/pages' },
    { title: 'Articles', value: stats.articles, icon: <Article />, color: '#10b981', path: '/articles' },
    { title: 'Centres', value: stats.centres, icon: <LocalHospital />, color: '#f59e0b', path: '/centres' },
    { title: 'Temoignages en attente', value: stats.temoignagesPending, icon: <Comment />, color: '#ef4444', path: '/temoignages' },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight="bold">
          Tableau de bord
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/pages/new')}
            sx={{ mr: 1 }}
          >
            Nouvelle page
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => navigate('/articles/new')}
          >
            Nouvel article
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} mb={4}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate(stat.path)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {stat.title}
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: `${stat.color}20`,
                      borderRadius: 2,
                      p: 1.5,
                      color: stat.color
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Pages recentes
              </Typography>
              <List>
                {recentPages.map((page) => (
                  <ListItem
                    key={page._id}
                    button
                    onClick={() => navigate(`/pages/${page._id}`)}
                  >
                    <ListItemIcon>
                      <Pages color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={page.title}
                      secondary={new Date(page.updatedAt).toLocaleDateString('fr-FR')}
                    />
                    <Chip
                      size="small"
                      label={page.status}
                      color={page.status === 'published' ? 'success' : 'default'}
                    />
                  </ListItem>
                ))}
              </List>
              <Button fullWidth onClick={() => navigate('/pages')}>
                Voir toutes les pages
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Actions rapides
              </Typography>
              <List>
                <ListItem button onClick={() => navigate('/pages/new')}>
                  <ListItemIcon><Add color="primary" /></ListItemIcon>
                  <ListItemText primary="Creer une nouvelle page" />
                </ListItem>
                <ListItem button onClick={() => navigate('/articles/new')}>
                  <ListItemIcon><Add color="secondary" /></ListItemIcon>
                  <ListItemText primary="Rediger un article" />
                </ListItem>
                <ListItem button onClick={() => navigate('/centres/new')}>
                  <ListItemIcon><LocalHospital color="warning" /></ListItemIcon>
                  <ListItemText primary="Ajouter un centre" />
                </ListItem>
                <ListItem button onClick={() => navigate('/temoignages')}>
                  <ListItemIcon><Comment color="error" /></ListItemIcon>
                  <ListItemText
                    primary="Moderer les temoignages"
                    secondary={stats.temoignagesPending > 0 ? `${stats.temoignagesPending} en attente` : ''}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
